import React, { useMemo } from "react";
import MaterialTable from "material-table";
import EditIcon from "@material-ui/icons/Edit";
import { getLookupForDataSortedByField } from "../../../utils";
import { fmtDate, fmtPaValue, fmtResult, isPaParameter } from "./format";
import DateRangeColumnFilter, {
  makeDateRangeFilter,
} from "./DateRangeColumnFilter";

const tableOptions = {
  emptyRowsWhenPaging: false,
  exportAllData: true,
  columnsButton: true,
  exportButton: { csv: true },
  exportFileName: "water-quality-data",
  pageSize: 30,
  pageSizeOptions: [10, 30, 60, 120],
  padding: "dense",
  searchFieldAlignment: "left",
  showTitle: true,
  maxBodyHeight: "560px",
  filtering: true,
  headerStyle: {
    whiteSpace: "nowrap",
    fontWeight: 600,
  },
  cellStyle: {
    whiteSpace: "nowrap",
  },
};

const fmtTableDate = (v) =>
  fmtDate(v, { fallback: "", pattern: "yyyy-MM-dd HH:mm" });
const fmtTableResult = (v) => fmtResult(v, { fallback: "" });

const WqDataTable = ({ rows, isLoading, onEditEvent }) => {
  // result_value_entered is the value exactly as typed (numeric → string,
  // trailing zeros intact); result_value is the float mirror. Normalize so
  // the column (and the CSV export, which reads the raw field) always has a
  // value even on payloads predating the entered column.
  const tableRows = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        result_value_entered: r.result_value_entered ?? r.result_value,
      })),
    [rows]
  );

  const parameterLookup = useMemo(
    () => getLookupForDataSortedByField(rows, "parameter_abbrev"),
    [rows]
  );
  const parameterNameLookup = useMemo(
    () => getLookupForDataSortedByField(rows, "parameter_name"),
    [rows]
  );
  const unitLookup = useMemo(
    () => getLookupForDataSortedByField(rows, "unit_abbrev"),
    [rows]
  );
  const sourceLookup = useMemo(
    () => getLookupForDataSortedByField(rows, "data_source"),
    [rows]
  );
  const equipmentLookup = useMemo(
    () => getLookupForDataSortedByField(rows, "equipment"),
    [rows]
  );
  const sampledByLookup = useMemo(
    () => getLookupForDataSortedByField(rows, "sampled_by"),
    [rows]
  );
  // edited_by is null on most rows (only populated after an edit), so the
  // shared helper would otherwise emit a phantom blank checkbox. Filter
  // empties out before building the lookup.
  const editedByLookup = useMemo(
    () =>
      getLookupForDataSortedByField(
        rows.filter((r) => r.edited_by),
        "edited_by"
      ),
    [rows]
  );

  const columns = useMemo(
    () => [
      { title: "Well Name", field: "well_name", type: "string" },
      {
        title: "Parameter",
        field: "parameter_abbrev",
        type: "string",
        lookup: parameterLookup,
      },
      {
        title: "Parameter Name",
        field: "parameter_name",
        type: "string",
        lookup: parameterNameLookup,
      },
      {
        title: "Date",
        field: "collect_timestamp",
        filterComponent: DateRangeColumnFilter,
        customFilterAndSearch: makeDateRangeFilter("collect_timestamp"),
        render: (r) => fmtTableDate(r.collect_timestamp),
        customSort: (a, b) =>
          new Date(a.collect_timestamp) - new Date(b.collect_timestamp),
      },
      {
        title: "Result",
        field: "result_value_entered",
        type: "numeric",
        // Entered values are strings ("2.0") to preserve precision — sort
        // and filter on the parsed number, render exact.
        customSort: (a, b) =>
          (parseFloat(a.result_value_entered) || 0) -
          (parseFloat(b.result_value_entered) || 0),
        customFilterAndSearch: (term, r) =>
          String(r.result_value_entered ?? "").includes(term),
        render: (r) => {
          if (isPaParameter(r.aqsci_parameter_ndx)) {
            return fmtPaValue(r.result_value_entered, { fallback: "" });
          }
          return r.is_nondetect
            ? `ND (${fmtTableResult(r.result_value_entered)})`
            : fmtTableResult(r.result_value_entered);
        },
      },
      {
        title: "Unit",
        field: "unit_abbrev",
        type: "string",
        lookup: unitLookup,
      },
      {
        title: "ND?",
        field: "is_nondetect",
        type: "boolean",
      },
      {
        title: "Equipment",
        field: "equipment",
        type: "string",
        lookup: equipmentLookup,
      },
      { title: "Lab / Other", field: "equip_lab_other", type: "string" },
      {
        title: "Sampled By",
        field: "sampled_by",
        type: "string",
        lookup: sampledByLookup,
      },
      {
        title: "Source",
        field: "data_source",
        type: "string",
        lookup: sourceLookup,
      },
      { title: "Notes", field: "notes", type: "string" },
      {
        title: "Edited By",
        field: "edited_by",
        type: "string",
        lookup: editedByLookup,
        emptyValue: "",
      },
      {
        title: "Edited On",
        field: "edited_on",
        filterComponent: DateRangeColumnFilter,
        customFilterAndSearch: makeDateRangeFilter("edited_on"),
        render: (r) => fmtTableDate(r.edited_on),
        customSort: (a, b) =>
          new Date(a.edited_on || 0) - new Date(b.edited_on || 0),
      },
    ],
    [
      parameterLookup,
      parameterNameLookup,
      unitLookup,
      sourceLookup,
      equipmentLookup,
      sampledByLookup,
      editedByLookup,
    ]
  );

  // Historical rows (data_ndx >= 900_000_000) live in data_wq_historical and
  // aren't editable through this app. Show the pencil disabled with a clear
  // tooltip so users understand why instead of being puzzled by missing icons.
  const actions = onEditEvent
    ? [
        (rowData) => {
          const isHistorical = rowData.data_ndx >= 900000000;
          return {
            icon: () => <EditIcon fontSize="small" />,
            tooltip: isHistorical
              ? "Historical record — not editable from this app"
              : "Edit this sampling event",
            onClick: isHistorical ? undefined : (_, row) => onEditEvent(row),
            disabled: isHistorical,
          };
        },
      ]
    : undefined;

  return (
    <MaterialTable
      title="Water Quality Measurements"
      columns={columns}
      data={tableRows}
      isLoading={isLoading}
      options={tableOptions}
      actions={actions}
    />
  );
};

export default WqDataTable;
